import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useEffect, useRef, useState } from "react";
import CloseIcon from "../../assets/icons/close-icon";
import moment from "moment";
import { useFormikContext } from "formik";

const EXPIRING_SOON_MONTHS = 3;

// Global pill colours, shown as bold text on a light tint of the same colour
const EXPIRY_TONES = {
  expired: {
    text: "text-[var(--pill-badge-red-bg)]",
    dot: "bg-[var(--pill-badge-red-bg)]",
    row: "bg-[color-mix(in_srgb,var(--pill-badge-red-bg)_6%,white)]",
  },
  expiring: {
    text: "text-[var(--pill-badge-yellow-bg)]",
    dot: "bg-[var(--pill-badge-yellow-bg)]",
    row: "",
  },
  safe: {
    text: "text-[var(--slide-panel-label)]",
    dot: "bg-[var(--pill-badge-green-bg)]",
    row: "",
  },
};

const STOCK_TONES = {
  "In Stock":
    "bg-[color-mix(in_srgb,var(--pill-badge-green-bg)_12%,white)] text-[var(--pill-badge-green-bg)]",
  "Out of Stock":
    "bg-[color-mix(in_srgb,var(--pill-badge-red-bg)_12%,white)] text-[var(--pill-badge-red-bg)]",
  default:
    "bg-[color-mix(in_srgb,var(--pill-badge-yellow-bg)_15%,white)] text-[var(--pill-badge-yellow-bg)]",
};

const getExpiryInfo = (value) => {
  const expDate = moment(value);
  if (!value || !expDate.isValid()) return null;
  const today = moment();
  if (expDate.isBefore(today, "day")) {
    const days = today.diff(expDate, "days");
    return {
      tone: "expired",
      date: expDate,
      hint: days > 0 ? `Expired ${days} day${days === 1 ? "" : "s"} ago` : "Expired today",
    };
  }
  const months = Math.floor(expDate.diff(today, "months", true));
  const days = expDate.diff(today, "days");
  return {
    tone: months < EXPIRING_SOON_MONTHS ? "expiring" : "safe",
    date: expDate,
    hint:
      months >= 1
        ? `Expires in ${months} month${months === 1 ? "" : "s"}`
        : `Expires in ${days} day${days === 1 ? "" : "s"}`,
  };
};

const isPercentCol = (col) =>
  (col?.label || "").includes("%") ||
  (col?.value || "").toLowerCase().includes("percentage");

const isCurrencyCol = (col) =>
  /mrp|price|(?<!tax_)rate/i.test(col?.value || "") ||
  /\b(mrp|pr|sp|rate|price)\b/i.test(col?.label || "");

const formatValue = (col, raw) => {
  if (raw === null || raw === undefined || raw === "") return "—";
  const num = Number(raw);
  if (typeof raw === "boolean" || !Number.isFinite(num)) return raw;
  if (isPercentCol(col)) return `${num.toFixed(2)}%`;
  if (isCurrencyCol(col)) return `₹${num.toFixed(2)}`;
  return raw;
};

// Fixed, compact column set. Each slot picks its label/key from the configured
// heading when present, otherwise falls back to the default below.
const COLUMN_SLOTS = [
  {
    match: (v, l) => v === "item_batch_number" || /batch/.test(l),
    fallback: { label: "Batch No.", value: "item_batch_number" },
    alignment: "left",
  },
  {
    match: (v, l) => /uom/.test(v) || /uom/.test(l),
    fallback: { label: "UOM", value: "item_uom" },
    alignment: "left",
  },
  {
    match: (v, l) => v === "item_exp_date" || /exp/.test(l),
    fallback: { label: "Exp Date", value: "item_exp_date" },
    alignment: "left",
  },
  {
    match: (v, l) => v === "stock_status" || /status/.test(l),
    fallback: { label: "Stock Status", value: "stock_status" },
    alignment: "left",
  },
  {
    match: (v, l) => /mrp/.test(v) || /mrp/.test(l),
    alignment: "right",
  },
  {
    match: (v, l) => /selling_price/.test(v) || /\bsp\b/.test(l),
    fallback: { label: "Final SP", value: "item_selling_price" },
    alignment: "right",
  },
  {
    match: (v, l) => /purchase_rate/.test(v) || /\bpr\b/.test(l),
    fallback: { label: "Final PR", value: "item_batch_final_purchase_rate" },
    alignment: "right",
  },
  {
    match: (v, l) => /margin/.test(v) || /margin/.test(l),
    fallback: { label: "Margin%", value: "item_margin_percentage" },
    alignment: "right",
  },
  {
    match: (v, l) => /scheme/.test(v) || /scheme/.test(l),
    alignment: "right",
  },
  {
    match: (v, l) => /schedule/.test(v) || /schedule/.test(l),
    fallback: { label: "Schedule", value: "item_schedule" },
    alignment: "center",
  },
  {
    match: (v, l) => /disc/.test(v) || /disc/.test(l),
    fallback: { label: "Disc%", value: "discount_percentage" },
    alignment: "right",
  },
  {
    match: (v, l) => /gst|tax_rate/.test(v) || /gst/.test(l),
    fallback: { label: "GST%", value: "tax_rate" },
    alignment: "right",
  },
  {
    // Pack qty with loose qty stacked underneath
    match: (v) => v === "item_pack_qty",
    fallback: { value: "item_pack_qty" },
    label: "Available Qty",
    alignment: "right",
  },
];

const buildColumns = (heading) =>
  COLUMN_SLOTS.map((slot) => {
    const found = heading.find((col) =>
      slot.match(
        (col?.value || "").toLowerCase(),
        (col?.label || "").toLowerCase()
      )
    );
    if (found?.enabled === false) return null;
    const col = found || slot.fallback;
    if (!col) return null;
    return {
      ...col,
      label: slot.label || col.label,
      alignment: slot.alignment,
    };
  }).filter(Boolean);

const getAlignment = (col) =>
  col?.alignment === "right"
    ? "text-right justify-end"
    : col?.alignment === "center"
    ? "text-center justify-center"
    : "text-left justify-start";

const BatchSelectionModal = ({
  isOpen,
  setIsOpen,
  batches = [],
  onSelect,
  heading = [],
  dynamicKey = "data",
}) => {
  const { values } = useFormikContext();
  const item_id = values?.tempRowData?.item_id;
  const itemName = values?.tempRowData?.item_name;
  const itemCode = values?.tempRowData?.item_code;

  const [selectedIndex, setSelectedIndex] = useState(0);
  const rowRefs = useRef([]);
  // Only keyboard navigation should scroll the list; mouse hover must not
  const scrollOnSelectRef = useRef(true);

  const filteredBatches = batches.filter(
    (batch) =>
      !values?.[dynamicKey]?.some(
        (entry) =>
          entry.item_id === item_id &&
          entry.item_batch_number === batch.item_batch_number &&
          entry?.item_rack_location === batch?.item_rack_location
      )
  );

  const columns = buildColumns(heading);

  const closeModal = () => {
    setIsOpen(false);
    setSelectedIndex(0);
  };

  const handleKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      scrollOnSelectRef.current = true;
      setSelectedIndex((prev) =>
        Math.min(prev + 1, filteredBatches.length - 1)
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      scrollOnSelectRef.current = true;
      setSelectedIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === "Enter") {
      if (filteredBatches[selectedIndex]) {
        onSelect(filteredBatches[selectedIndex]);
      }
    } else if (e.key === "Escape") {
      setIsOpen(false);
    }
  };

  // Listener reads the latest handler so it never acts on stale batches/props
  const keyHandlerRef = useRef(handleKeyDown);
  keyHandlerRef.current = handleKeyDown;

  useEffect(() => {
    if (!isOpen) return;
    const listener = (e) => keyHandlerRef.current(e);
    window.addEventListener("keydown", listener);
    return () => window.removeEventListener("keydown", listener);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) setSelectedIndex(0);
  }, [isOpen]);

  useEffect(() => {
    if (scrollOnSelectRef.current && rowRefs.current[selectedIndex]) {
      rowRefs.current[selectedIndex].scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [selectedIndex]);

  const renderCell = (col, batch) => {
    const value = batch?.[col.value];

    if (col.value === "item_batch_number") {
      const expiry = getExpiryInfo(batch?.item_exp_date);
      return (
        <div className="flex flex-col leading-tight">
          <span className="font-semibold text-[13px] text-[var(--create-table-row-fg-color)]">
            {value || "—"}
          </span>
          {(expiry || batch?.item_rack_location) && (
            <span className="text-[11px] mt-[2px] text-[var(--slide-panel-label)]">
              {expiry && (
                <span className={EXPIRY_TONES[expiry.tone].text}>
                  {expiry.hint}
                </span>
              )}
              {expiry && batch?.item_rack_location && " · "}
              {batch?.item_rack_location && `Rack/Bin: ${batch.item_rack_location}`}
            </span>
          )}
        </div>
      );
    }

    if (col.value === "item_exp_date") {
      const expiry = getExpiryInfo(value);
      if (!expiry) return "—";
      const tone = EXPIRY_TONES[expiry.tone];
      return (
        <span className="inline-flex items-center gap-[6px]">
          <span className={`w-[7px] h-[7px] rounded-full ${tone.dot}`} />
          <span className={expiry.tone === "safe" ? "" : `font-medium ${tone.text}`}>
            {expiry.date.format("DD MMM YYYY")}
          </span>
        </span>
      );
    }

    if (col.value === "stock_status") {
      if (!value) return "—";
      return (
        <span
          className={`inline-flex items-center px-[8px] py-[2px] rounded-full text-[11px] font-bold ${
            STOCK_TONES[value] || STOCK_TONES.default
          }`}
        >
          {value}
        </span>
      );
    }

    if (col.value === "item_pack_qty") {
      return (
        <div className="flex flex-col items-end leading-tight">
          <span className="font-semibold">{value ?? 0} Packs</span>
          <span className="text-[11px] text-[var(--slide-panel-label)] mt-[2px]">
            Loose: {batch?.item_loose_qty ?? 0}
          </span>
        </div>
      );
    }

    return formatValue(col, value);
  };

  return (
    <Transition show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="fixed inset-0 z-50 overflow-y-auto"
        onClose={closeModal}
      >
        <div className="flex items-center justify-center min-h-screen px-4 text-center">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-100"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-black opacity-50 z-40" />
          </Transition.Child>

          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0 translate-y-2 scale-[0.99]"
            enterTo="opacity-100 translate-y-0 scale-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0 scale-100"
            leaveTo="opacity-0 translate-y-2 scale-[0.99]"
          >
            <div
              tabIndex={0}
              className="relative z-50 flex flex-col w-[95%] max-w-[1500px] max-h-[80vh] min-h-[22rem] mx-auto bg-white rounded-[14px] shadow-[0_24px_64px_-12px_rgba(15,23,42,0.35)] text-left overflow-hidden focus:outline-none"
            >
              {/* Header */}
              <div className="flex items-center justify-between gap-4 px-6 py-4 border-b border-[var(--create-table-main-border)]">
                <div className="min-w-0">
                  <Dialog.Title className="text-[16px] font-semibold text-[var(--slide-panel-h-fg)]">
                    Select Batch
                  </Dialog.Title>
                  <div className="flex items-center gap-2 mt-[2px] text-[12px] text-[var(--slide-panel-label)] truncate">
                    {itemName && (
                      <span className="font-medium text-[var(--secondary)] truncate">
                        {itemName}
                      </span>
                    )}
                    {itemCode && (
                      <>
                        <span className="text-[var(--gray-200)]">•</span>
                        <span>{itemCode}</span>
                      </>
                    )}
                    {(itemName || itemCode) && (
                      <span className="text-[var(--gray-200)]">•</span>
                    )}
                    <span>
                      {filteredBatches.length} batch
                      {filteredBatches.length === 1 ? "" : "es"} available
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-5 shrink-0">
                  <div className="hidden md:flex items-center gap-4 text-[12px] text-[var(--slide-panel-label)]">
                    <span className="inline-flex items-center gap-[6px]">
                      <span className={`w-[8px] h-[8px] rounded-full ${EXPIRY_TONES.expired.dot}`} />
                      Expired
                    </span>
                    <span className="inline-flex items-center gap-[6px]">
                      <span className={`w-[8px] h-[8px] rounded-full ${EXPIRY_TONES.expiring.dot}`} />
                      Expiring within {EXPIRING_SOON_MONTHS} months
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setIsOpen(false)}
                    title="Close (Esc)"
                    className="flex items-center justify-center w-[32px] h-[32px] rounded-[8px] text-[var(--slide-panel-label)] hover:bg-[var(--create-table-row-hover-bg)] transition-colors"
                  >
                    <CloseIcon width="12px" height="12px" fill="currentColor" />
                  </button>
                </div>
              </div>

              {/* Table */}
              <div className="flex-1 overflow-auto px-3">
                {filteredBatches.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full min-h-[14rem] text-center">
                    <span className="text-[14px] font-medium text-[var(--secondary)]">
                      No batches available
                    </span>
                    <span className="text-[12px] text-[var(--slide-panel-label)] mt-1">
                      All batches of this item are already added, or none are in stock.
                    </span>
                  </div>
                ) : (
                  <table className="w-full border-separate border-spacing-0">
                    <thead className="sticky top-0 z-[10] bg-[var(--create-table-batch-con-bg)]">
                      <tr>
                        {columns.map((col, idx) => (
                          <th
                            key={idx}
                            className="px-3 py-3 border-b border-[var(--create-table-main-border)] whitespace-nowrap text-[12px] font-[var(--create-table-header-fw)] uppercase tracking-[0.03em] text-[var(--create-table-header-bg)]"
                          >
                            <div className={`flex items-center ${getAlignment(col)}`}>
                              {col?.label}
                            </div>
                          </th>
                        ))}
                        <th className="w-[32px] border-b border-[var(--create-table-main-border)]" />
                      </tr>
                    </thead>
                    <tbody>
                      {filteredBatches.map((batch, index) => {
                        const isSelected = selectedIndex === index;
                        const isOut = batch?.stock_status === "Out of Stock";
                        const expiry = getExpiryInfo(batch?.item_exp_date);
                        const rowTint =
                          !isSelected && expiry?.tone === "expired"
                            ? EXPIRY_TONES.expired.row
                            : "";

                        return (
                          <tr
                            ref={(el) => (rowRefs.current[index] = el)}
                            key={`${batch.item_batch_number}-${batch.item_rack_location}-${index}`}
                            onClick={() => {
                              onSelect(batch);
                              setSelectedIndex(0);
                            }}
                            onMouseMove={() => {
                              if (selectedIndex !== index) {
                                scrollOnSelectRef.current = false;
                                setSelectedIndex(index);
                              }
                            }}
                            className={`group cursor-pointer transition-colors ${
                              isSelected
                                ? "bg-[var(--create-table-row-sel-bg)]"
                                : rowTint
                            } ${isOut ? "opacity-50" : ""}`}
                          >
                            {columns.map((col, colIndex) => (
                              <td
                                key={colIndex}
                                style={{
                                  boxShadow:
                                    isSelected && colIndex === 0
                                      ? "inset 3px 0 0 var(--primary)"
                                      : undefined,
                                }}
                                className={`px-3 py-[10px] border-b border-[var(--create-table-border)] whitespace-nowrap text-[13px] tabular-nums text-[var(--create-table-row-fg-color)] ${getAlignment(col)}`}
                              >
                                {renderCell(col, batch)}
                              </td>
                            ))}
                            <td className="pr-3 border-b border-[var(--create-table-border)] text-right">
                              <span
                                className={`text-[16px] leading-none transition-all ${
                                  isSelected
                                    ? "text-primary translate-x-[2px]"
                                    : "text-[var(--gray-200)] group-hover:text-[var(--slide-panel-label)]"
                                }`}
                              >
                                ›
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>

              {/* Footer */}
              <div className="flex items-center gap-4 px-6 py-[10px] border-t border-[var(--create-table-main-border)] bg-[var(--slide-panel-special-bg)] text-[11px] text-[var(--slide-panel-label)]">
                <span className="inline-flex items-center gap-[6px]">
                  <kbd className="px-[6px] py-[1px] rounded border border-[var(--light-border-color)] bg-white text-[var(--slide-panel-label)] font-sans">↑</kbd>
                  <kbd className="px-[6px] py-[1px] rounded border border-[var(--light-border-color)] bg-white text-[var(--slide-panel-label)] font-sans">↓</kbd>
                  Navigate
                </span>
                <span className="inline-flex items-center gap-[6px]">
                  <kbd className="px-[6px] py-[1px] rounded border border-[var(--light-border-color)] bg-white text-[var(--slide-panel-label)] font-sans">Enter</kbd>
                  Select
                </span>
                <span className="inline-flex items-center gap-[6px]">
                  <kbd className="px-[6px] py-[1px] rounded border border-[var(--light-border-color)] bg-white text-[var(--slide-panel-label)] font-sans">Esc</kbd>
                  Close
                </span>
              </div>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
};

export default BatchSelectionModal;
